import * as dates from '@simplybenefits/sb-dates';
import moment from 'moment';
import validateInput from "../../../services/FormValidation/validateInput.js";
import i18n from "../../../localization/i18n.js";
import tokens from "../../../localization/tokens.js";
import * as localizationMaps from "../../../localization/maps.js";

/**
 * Calculates the eligibility date based on the cohabitation date.
 * If no cohabitation date is provided, sets eligibility to one year from now.
 * 
 * @param {string} cohabDate Cohabitation date in YYYY-MM-DD format (optional)
 * @returns {Date} Eligibility date
 */
const getEligibilityDate = cohabDate => {
  if (!cohabDate) {
    const oneYearFromNow = new Date();
    oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
    return dates.getStartOfDay(oneYearFromNow);
  }
  const oneYearFromCohab = new Date(cohabDate);
  oneYearFromCohab.setFullYear(oneYearFromCohab.getFullYear() + 1);
  return dates.getStartOfDay(oneYearFromCohab);
};

/**
 * Checks if the dependent is eligible based on their eligibility date -> used in a common law relationship only.
 * 
 * @param {Date} eligibilityDate The dependent's eligibility date
 * @returns {boolean} True if eligible, false otherwise
 */
const isDependentEligible = eligibilityDate => {
  const now = dates.getUTC();
  return eligibilityDate < now;
};

/**
 * Validates all fields of the dependent form.
 * 
 * @param {object} dependent Dependent form data
 * @returns {Promise<boolean|object>} False if valid, object with errors otherwise
 */
const validateDependentForm = async dependent => {
  const {
    firstname,
    lastname,
    gender,
    birthdate,
    cohabDate,
    dateOfMarriage,
    email,
    separateCoverage,
    employer,
    insuranceCarrier,
    cert
  } = dependent;
  const relationship = localizationMaps.toEnglish("relationships", dependent.relationship).toLowerCase();
  const errors = {}; // Object to store validation errors

  errors.firstnameError = await validateInput('dependentFirstname', firstname);
  errors.lastnameError = await validateInput('dependentLastname', lastname);
  errors.genderError = await validateInput('gender', gender);
  errors.emailError = email ? await validateInput('email', email) : false;
  const age = moment.utc().diff(birthdate, 'years');

  /*
   * Calculates dependent's age and performs additional birthdate validation for child/grandchild dependents.
   * - Calculates age difference in years between current date (moment.utc()) and birthdate.
   * - Checks for existing birthdate validation errors
   * - If no previous birthdate errors and:
   *   - Age is greater than 25 years old,
   *   - Dependent is not disabled,
   *   - Relationship is 'child' or 'grandchild',
   * then sets a new birthdate error message using translated text indicating dependents must be under 26.
   */
  errors.birthdateError = await validateInput('birthdate', birthdate, i18n.locale);
  if (!errors.birthdateError && age > 25 && ['child', 'grandchild'].includes(relationship)) {
    errors.birthdateError = i18n.t(tokens.labels.validations.childDependentsUnder26);
  }

  /*
   * Handles cohabitation date validation and eligibility checks specifically for common-law relationships.
   * - Validates cohabitation date format (if applicable).
   * - Calculates eligibility date based on cohabitation date.
   * - Checks if dependent is eligible based on eligibility date.
   * - Sets cohabitation eligibility date error if:
   *   - Cohabitation date is valid (no validation errors).
   *   - Dependent is ineligible based on calculated eligibility date.
   * - Provides a translated error message with formatted eligibility date for clarity.
   * - Sets cohabitation date error to false for non-common-law relationships to avoid unnecessary errors.
   */
  if (relationship === 'common law') {
    errors.cohabDateError = await validateInput('cohabDate', cohabDate);
    const eligibilityDate = getEligibilityDate(cohabDate);
    const isEligible = isDependentEligible(eligibilityDate);
    if (!errors.cohabDateError && !isEligible) {
      errors.cohabDateError = i18n.t(tokens.labels.validations.thisDependentNotEligibleCohabError, {
        eligibilityDate: dates.getStartOfDay(eligibilityDate, 'll', i18n.locale)
      });
    }
  } else {
    errors.cohabDateError = false;
  }
  if (relationship === "spouse") {
    errors.dateOfMarriageError = await validateInput('dateOfMarriage', dateOfMarriage);
  }
  errors.relationshipError = await validateInput('relationship', relationship);
  if (['spouse', 'common law'].includes(relationship) && separateCoverage) {
    errors.employerError = await validateInput('checkEmpty', employer || '');
    errors.insuranceCarrierError = await validateInput('checkEmpty', insuranceCarrier || '');
    errors.certError = await validateInput('checkEmpty', cert || '');
  } else {
    errors.employerError = false;
    errors.insuranceCarrierError = false;
    errors.certError = false;
  }

  // Check for any errors
  const hasErrors = Object.values(errors).some(Boolean);
  return hasErrors ? errors : false;
};
export default validateDependentForm;